import { useMemo } from 'react'
import { ROUTES } from '@/constants/routes'
import { consoleCopy, threadCopy } from '@/copy'
import { Alert } from '@/ui/Alert/Alert'
import { Avatar } from '@/ui/Avatar/Avatar'
import { Button } from '@/ui/Button/Button'
import { EmptyState } from '@/ui/EmptyState/EmptyState'
import { Link } from '@/ui/Link/Link'
import { Skeleton } from '@/ui/Loading/Loading'
import { useSkeletonDelay } from '@/ui/Loading/useSkeletonDelay'
import { Inline } from '@/ui/layout/Inline'
import { Stack } from '@/ui/layout/Stack'
import { Text } from '@/ui/layout/Text'
import { RowList } from '@/ui/RowList/RowList'
import { SectionHeader } from '@/ui/SectionHeader/SectionHeader'
import { lifecycleTone } from '@/ui/Status/lifecycle'
import { StatusBadge, StatusDot } from '@/ui/Status/Status'
import { useAgentLiveStates } from '../../hooks/useAgentLiveState'
import { useNow } from '../../hooks/useNow'
import type {
  AgentParticipant,
  HistoricalMessage,
  ThreadSummary,
  TopicResource,
} from '../../types/api'
import { RefreshFailed } from '../RefreshFailed'
import { ResourceRow } from './ResourceRow'
import styles from './ThreadPanel.module.css'
import { resolveParticipants, threadAgeDisplay } from './tags'

interface Props {
  topicId: string
  topicName?: string
  summary?: ThreadSummary
  /** Active view (GET .../threads/:id/resources): rows born in, used by,
   * scoped to, or workspace-shared with this thread. The sole band source;
   * undefined while it loads. */
  threadResources?: TopicResource[]
  resourcesError?: boolean
  onRetryResources?: () => void
  messages: HistoricalMessage[]
  agents: AgentParticipant[]
  workspaceId?: string
  onOpenTopicResources: () => void
}

/**
 * The redesigned thread side panel (§4): THREAD facts, PARTICIPANTS with
 * live agent state (no participation reasons — observed, not labeled),
 * and RESOURCES in scope bands — this thread's own rows at full
 * opacity, topic and workspace rows dimmed as inherited. Replaces the
 * 4-chip resource strip.
 *
 * Bands derive from the active view alone. Unioning the full topic pool
 * back in would defeat the born ∪ used filter: every new thread would
 * list the whole topic under "from #topic". Topic-scoped rows still
 * appear here when this thread born/used them (the active endpoint
 * returns them regardless of scope); their own columns classify them.
 */
export function ThreadPanel({
  topicId,
  topicName,
  summary,
  threadResources,
  resourcesError,
  onRetryResources,
  messages,
  agents,
  workspaceId,
  onOpenTopicResources,
}: Props) {
  const now = useNow()
  const { stateOf } = useAgentLiveStates(workspaceId)

  const parts = useMemo(
    () => resolveParticipants(summary?.participants ?? [], agents),
    [summary, agents],
  )

  // Scope bands: active-view rows classify by their own scope columns.
  const bands = useMemo(() => {
    const rows = threadResources ?? []
    return {
      thread: rows.filter((r) => r.thread_id != null),
      topic: rows.filter((r) => r.thread_id == null && !r.workspace_id),
      workspace: rows.filter((r) => r.workspace_id != null),
    }
  }, [threadResources])

  const status = summary
    ? summary.status === 'resolved'
      ? 'resolved'
      : summary.needs_you
        ? 'needs_you'
        : 'active'
    : null
  const startedAt = messages[0]?.timestamp
  const totalResources =
    bands.thread.length + bands.topic.length + bands.workspace.length
  const loading = threadResources === undefined && !resourcesError
  const showSkeleton = useSkeletonDelay(loading)

  return (
    <Stack gap="space-24">
      <section>
        <Stack gap="space-8">
          <SectionHeader as="h2">{threadCopy.panel.thread}</SectionHeader>
          <dl className={styles.facts}>
            <div className={styles.fact}>
              <Text as="dt" variant="caption" tone="text-ghost">
                {threadCopy.panel.status}
              </Text>
              <Text as="dd">
                {status ? (
                  <StatusBadge
                    tone={lifecycleTone(
                      status === 'needs_you' ? 'needs you' : status,
                    )}
                  >
                    {consoleCopy.threadStatus[status]}
                  </StatusBadge>
                ) : (
                  '—'
                )}
              </Text>
            </div>
            <div className={styles.fact}>
              <Text as="dt" variant="caption" tone="text-ghost">
                {threadCopy.panel.topic}
              </Text>
              <Text as="dd" tone="text-sub">
                {topicName ?? topicId}
              </Text>
            </div>
            <div className={styles.fact}>
              <Text as="dt" variant="caption" tone="text-ghost">
                {threadCopy.panel.started}
              </Text>
              <Text as="dd" tone="text-sub" numeric>
                {startedAt ? threadAgeDisplay(startedAt, now) : '—'}
              </Text>
            </div>
            <div className={styles.fact}>
              <Text as="dt" variant="caption" tone="text-ghost">
                {threadCopy.panel.messages}
              </Text>
              <Text as="dd" tone="text-sub" numeric>
                {summary?.message_count ?? messages.length}
              </Text>
            </div>
          </dl>
        </Stack>
      </section>

      <section>
        <Stack gap="space-8">
          <SectionHeader
            as="h2"
            count={parts.length > 0 ? parts.length : undefined}
          >
            {threadCopy.panel.participants}
          </SectionHeader>
          <ul className={styles.people}>
            {parts.map((p) => (
              <li key={p.id} className={styles.person}>
                <Avatar
                  name={p.name}
                  kind={p.isAgent ? 'agent' : 'human'}
                  size="md"
                />
                <Text tone="text-sub">{p.name}</Text>
                {p.isAgent && (
                  <Text
                    variant="caption"
                    tone="text-sub"
                    className={styles.end}
                  >
                    <StatusDot
                      tone={lifecycleTone(stateOf(p.id))}
                      label={consoleCopy.agentState[stateOf(p.id)]}
                    />
                  </Text>
                )}
              </li>
            ))}
          </ul>
        </Stack>
      </section>

      <section aria-busy={loading || undefined}>
        <Stack gap="space-8">
          <SectionHeader as="h2" count={totalResources}>
            {threadCopy.panel.resources}
          </SectionHeader>
          {loading ? (
            showSkeleton && <Skeleton lines={3} />
          ) : resourcesError && threadResources === undefined ? (
            <Alert tone="danger">
              <Inline gap="space-8" align="baseline">
                {threadCopy.view.resourcesFailed}
                <Button variant="outline" size="sm" onClick={onRetryResources}>
                  {threadCopy.message.retry}
                </Button>
              </Inline>
            </Alert>
          ) : (
            <>
              {resourcesError && onRetryResources && (
                <RefreshFailed
                  message={threadCopy.view.resourcesFailed}
                  onRetry={onRetryResources}
                />
              )}
              {bands.thread.length > 0 && (
                <>
                  <Text as="p" variant="caption" tone="text-mute">
                    {threadCopy.panel.thisThread}
                  </Text>
                  <RowList variant="plain">
                    {bands.thread.map((r) => (
                      <ResourceRow key={r.id} resource={r} meta={false} />
                    ))}
                  </RowList>
                </>
              )}
              {bands.topic.length > 0 && (
                <>
                  <Text as="p" variant="caption" tone="text-mute">
                    {threadCopy.panel.from(topicName ?? topicId)}
                  </Text>
                  <div className={styles.inherited}>
                    <RowList variant="plain">
                      {bands.topic.map((r) => (
                        <ResourceRow key={r.id} resource={r} meta={false} />
                      ))}
                    </RowList>
                  </div>
                </>
              )}
              {bands.workspace.length > 0 && (
                <>
                  <Text as="p" variant="caption" tone="text-mute">
                    {threadCopy.panel.workspace}
                  </Text>
                  <div className={styles.inherited}>
                    <RowList variant="plain">
                      {bands.workspace.map((r) => (
                        <ResourceRow key={r.id} resource={r} meta={false} />
                      ))}
                    </RowList>
                  </div>
                </>
              )}
              {totalResources === 0 && (
                <EmptyState
                  variant="inline"
                  description={threadCopy.panel.empty}
                />
              )}
            </>
          )}
          <Link
            href={`${ROUTES.topic(topicId)}?tab=resources`}
            onClick={(e) => {
              if (
                e.button === 0 &&
                !e.metaKey &&
                !e.ctrlKey &&
                !e.shiftKey &&
                !e.altKey
              ) {
                e.preventDefault()
                onOpenTopicResources()
              }
            }}
          >
            {threadCopy.panel.allTopicResources}
          </Link>
        </Stack>
      </section>
    </Stack>
  )
}
